import * as THREE from "three";

import type {
  LoomFairingFootprintParent,
  LoomJunctionFairingFootprintPreview,
} from "./loomJunctionFairingFootprintPreview";

export interface LoomJunctionFairingFootprintOverlayReceipt {
  readonly contactGuideCount: number;
  readonly guideCount: number;
  readonly parentCount: number;
  readonly shapeGuideCount: number;
  readonly signature: string;
}

function shapeLeadGeometry(
  curve: Extract<LoomJunctionFairingFootprintPreview, { kind: "available" }>["curves"][number],
  opposite: Extract<LoomJunctionFairingFootprintPreview, { kind: "available" }>["curves"][number],
): THREE.BufferGeometry {
  const positions: number[] = [];
  const stride = Math.max(1, Math.floor(curve.points.length / 12));
  for (let index = 0; index < curve.points.length; index += stride) {
    const source = curve.points[index]!, target = opposite.points[index]!;
    const start = new THREE.Vector3(source[0], source[1], source[2]);
    const towardOtherParent = new THREE.Vector3(target[0], target[1], target[2]).sub(start);
    const normalizedScale = THREE.MathUtils.clamp(
      ((curve.tangentScales[index] ?? 1) - 0.05) / 1.95, 0, 1,
    );
    const end = start.clone().addScaledVector(towardOtherParent, 0.07 + 0.2 * normalizedScale);
    positions.push(...start.toArray(), ...end.toArray());
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  return geometry;
}

export function buildLoomJunctionFairingFootprintOverlay(
  preview: Extract<LoomJunctionFairingFootprintPreview, { kind: "available" }>,
  colors: Readonly<Record<LoomFairingFootprintParent, string>>,
): { group: THREE.Group; receipt: LoomJunctionFairingFootprintOverlayReceipt } {
  const group = new THREE.Group();
  group.name = "Provisional fairing intent guides";
  group.userData = {
    loomAuthority: preview.authority,
    loomFairingFootprintSignature: preview.signature,
    loomVisualRole: "fairing-intent-projection",
  };
  let shapeGuideCount = 0;
  preview.curves.forEach((curve) => {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(curve.points.flat(), 3));
    const material = new THREE.LineBasicMaterial({
      color: colors[curve.parent],
      depthTest: false,
      depthWrite: false,
      opacity: 0.82,
      transparent: true,
    });
    material.toneMapped = false;
    const line = new THREE.LineLoop(geometry, material);
    line.name = `${curve.side} ${curve.parent} fairing footprint`;
    line.renderOrder = 38;
    line.userData = {
      loomFairingFootprintParent: curve.parent,
      loomFairingFootprintSide: curve.side,
      loomOcclusionPolicy: "always-visible-draft-guide",
      loomVisualRole: "fairing-footprint-guide",
    };
    line.raycast = () => undefined;
    group.add(line);
  });
  preview.curves.forEach((curve) => {
    const opposite = preview.curves.find((candidate) => (
      candidate.side === curve.side && candidate.parent !== curve.parent
    ));
    if (!opposite) return;
    const material = new THREE.LineBasicMaterial({
      color: colors[curve.parent], depthTest: false, depthWrite: false,
      opacity: 0.46, transparent: true,
    });
    material.toneMapped = false;
    const guide = new THREE.LineSegments(shapeLeadGeometry(curve, opposite), material);
    guide.name = `${curve.side} ${curve.parent} fairing shape lead`;
    guide.renderOrder = 37;
    guide.userData = {
      loomFairingFootprintParent: curve.parent,
      loomFairingFootprintSide: curve.side,
      loomOcclusionPolicy: "always-visible-draft-guide",
      loomVisualRole: "fairing-shape-lead-guide",
    };
    guide.raycast = () => undefined;
    group.add(guide);
    shapeGuideCount += 1;
  });
  const contactGuideCount = preview.curves.length;
  return {
    group,
    receipt: {
      contactGuideCount,
      guideCount: contactGuideCount + shapeGuideCount,
      parentCount: new Set(preview.curves.map(({ parent }) => parent)).size,
      shapeGuideCount,
      signature: preview.signature,
    },
  };
}
